import * as React from 'react'
import { toast } from 'sonner'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { appTitle } from '@/shared/config/app-config'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
}

const INSTALL_TOAST_ID = 'pwa-install'
const OFFLINE_READY_TOAST_ID = 'pwa-offline-ready'
const UPDATE_TOAST_ID = 'pwa-update'

type PwaServiceWorkerContextValue = {
  isOfflineReady: boolean
  needsRefresh: boolean
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>
}

const PwaServiceWorkerContext =
  React.createContext<PwaServiceWorkerContextValue | null>(null)

export function PwaServiceWorkerController({
  children,
}: {
  children: React.ReactNode
}) {
  const { needRefresh, offlineReady, updateServiceWorker } = useRegisterSW()
  const [needsRefresh] = needRefresh
  const [isOfflineReady] = offlineReady

  return (
    <PwaServiceWorkerContext.Provider
      value={{
        isOfflineReady,
        needsRefresh,
        updateServiceWorker,
      }}
    >
      {children}
    </PwaServiceWorkerContext.Provider>
  )
}

export function PwaInstallPromptController() {
  const { isOfflineReady, needsRefresh, updateServiceWorker } =
    usePwaServiceWorker()

  useInstallPrompt()
  useOfflineReadyToast(isOfflineReady)
  useUpdateToast({ needsRefresh, updateServiceWorker })

  return null
}

function usePwaServiceWorker() {
  const context = React.useContext(PwaServiceWorkerContext)

  if (!context) {
    throw new Error(
      'PWA install prompt controller must be rendered inside PwaServiceWorkerController',
    )
  }

  return context
}

function useInstallPrompt() {
  React.useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      const prompt = event as BeforeInstallPromptEvent

      toast(`Install ${appTitle}`, {
        id: INSTALL_TOAST_ID,
        description:
          'Install the app shell for faster launches and standalone use.',
        duration: Infinity,
        action: {
          label: 'Install',
          onClick: () => {
            void promptForInstall(prompt, () => {
              // install completed
            })
          },
        },
        cancel: {
          label: 'Later',
          onClick: () => {
            // dismissed via the cancel button
          },
        },
      })
    }

    const handleAppInstalled = () => {
      toast.dismiss(INSTALL_TOAST_ID)
      toast.success(`${appTitle} installed`, {
        description: 'You can now launch it from your home screen or app list.',
      })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      )
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])
}

function useOfflineReadyToast(isOfflineReady: boolean) {
  const hasShownRef = React.useRef(false)

  if (isOfflineReady && !hasShownRef.current) {
    hasShownRef.current = true
    toast.success('Offline shell ready', {
      id: OFFLINE_READY_TOAST_ID,
      description:
        'Static app assets are cached. Live document data still needs a network connection.',
    })
  }
}

function useUpdateToast({
  needsRefresh,
  updateServiceWorker,
}: {
  needsRefresh: boolean
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>
}) {
  const lastNeedsRefreshRef = React.useRef(needsRefresh)
  const toastShownRef = React.useRef(false)

  if (needsRefresh !== lastNeedsRefreshRef.current) {
    lastNeedsRefreshRef.current = needsRefresh

    if (!needsRefresh) {
      toastShownRef.current = false
      toast.dismiss(UPDATE_TOAST_ID)
    }
  }

  if (needsRefresh && !toastShownRef.current) {
    toastShownRef.current = true
    toast('Update available', {
      id: UPDATE_TOAST_ID,
      description: `A newer version of ${appTitle} is ready to load.`,
      duration: Infinity,
      action: {
        label: 'Reload',
        onClick: () => {
          void updateServiceWorker(true)
        },
      },
      cancel: {
        label: 'Later',
        onClick: () => {
          toastShownRef.current = false
          toast.dismiss(UPDATE_TOAST_ID)
        },
      },
    })
  }
}

async function promptForInstall(
  installPrompt: BeforeInstallPromptEvent,
  onComplete: () => void,
) {
  await installPrompt.prompt()
  await installPrompt.userChoice
  onComplete()
}
